import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../services/api';
import type { AdminUserDetail, AdminUserJobSummary, AdminUserResumeSummary } from '../../services/api';
import { ArrowLeft, Save, Loader2, Briefcase, FileText, Gift } from 'lucide-react';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';

const EDITABLE_FIELDS = [
  { key: 'role', label: 'Role', options: ['user', 'admin', 'snappy', 'test'] },
  { key: 'status', label: 'Status', options: ['trial', 'active', 'hibernating', 'cancelled', 'expired'] },
  { key: 'plan', label: 'Plan', options: ['free', 'consumer', 'enterprise'] },
  { key: 'org_id', label: 'Organization ID', type: 'text' },
] as const;

export function UserDetail() {
  const { userid } = useParams<{ userid: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const loadUser = useCallback(() => {
    if (!userid) return;
    setLoading(true);
    api.getUser(userid)
      .then(setData)
      .catch(() => setMessage({ type: 'error', text: 'Failed to load user' }))
      .finally(() => setLoading(false));
  }, [userid]);

  useEffect(() => { loadUser(); }, [loadUser]);
  useAutoRefresh(loadUser);

  const handleFieldChange = (key: string, value: string) => {
    setEdits(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    if (Object.keys(edits).length === 0) return;

    const confirmed = window.confirm(`Update ${Object.keys(edits).length} field(s)?`);
    if (!confirmed) return;

    setSaving(true);
    setMessage(null);
    try {
      await api.updateUser(userid!, edits);
      setMessage({ type: 'success', text: 'User updated successfully' });
      setEdits({});
      // Reload
      const refreshed = await api.getUser(userid!);
      setData(refreshed);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } };
      setMessage({ type: 'error', text: axiosErr?.response?.data?.message || 'Failed to update user' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-8 h-8 animate-spin text-gray-500" />
      </div>
    );
  }

  const user = data?.user || {};

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button
        onClick={() => navigate('/admin/f47ac10b58cc/users')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Users
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{user.email || 'Unknown User'}</h1>
          <p className="text-sm text-gray-500">{user.userhandle} &middot; {user.userid}</p>
        </div>
        {Object.keys(edits).length > 0 && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes ({Object.keys(edits).length})
          </button>
        )}
      </div>

      {message && (
        <div className={`mb-4 p-3 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {message.text}
        </div>
      )}

      {/* Editable Fields */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Account Settings</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {EDITABLE_FIELDS.map(field => {
            const currentValue = edits[field.key] ?? user[field.key] ?? '';
            const isEdited = field.key in edits;

            if ('options' in field) {
              return (
                <div key={field.key}>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    {field.label} {isEdited && <span className="text-primary-600">*</span>}
                  </label>
                  <select
                    value={currentValue}
                    onChange={(e) => handleFieldChange(field.key, e.target.value)}
                    className={`w-full border rounded-lg px-3 py-2 ${isEdited ? 'border-primary-500 bg-primary-50' : 'border-gray-300'}`}
                  >
                    <option value="">-</option>
                    {field.options.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              );
            }

            return (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-600 mb-1">
                  {field.label} {isEdited && <span className="text-primary-600">*</span>}
                </label>
                <input
                  type="text"
                  value={currentValue}
                  onChange={(e) => handleFieldChange(field.key, e.target.value)}
                  className={`w-full border rounded-lg px-3 py-2 ${isEdited ? 'border-primary-500 bg-primary-50' : 'border-gray-300'}`}
                />
              </div>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <h3 className="text-sm font-medium text-gray-600 mb-2">Quick Actions</h3>
          <div className="flex gap-2">
            {(edits['plan'] ?? user['plan']) !== 'free' ? (
              <button
                onClick={() => {
                  setEdits(prev => ({
                    ...prev,
                    plan: 'free',
                    status: 'active',
                  }));
                }}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200"
              >
                <Gift className="w-4 h-4" />
                Grant Free Plan
              </button>
            ) : (
              <span className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-100 text-green-700 rounded-lg">
                <Gift className="w-4 h-4" />
                Free Plan (pending save)
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Read-only Fields */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">User Info (Read Only)</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {['userid', 'email', 'userhandle', 'sub_provider', 'sub_id', 'stripe_customer_id', 'stripe_subscription_id', 'marketing_email_optin', 'createdAt', 'updatedAt'].map(key => (
            user[key] !== undefined && (
              <div key={key} className="flex justify-between py-1 border-b border-gray-100">
                <span className="text-gray-500">{key}</span>
                <span className={`font-mono text-gray-800${key === 'userid' ? '' : ' truncate max-w-[60%]'}`}>{String(user[key])}</span>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Jobs Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Jobs ({data?.job_count || 0})
          </h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(data?.jobs_summary || []).map((job: AdminUserJobSummary) => (
              <div key={job.jobid} className="flex items-center justify-between text-sm py-1 border-b border-gray-100">
                <span className="truncate">{job.jobtitle || job.jobcompany || job.jobid}</span>
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100">{job.jobphase}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Resumes ({data?.resume_count || 0})
          </h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {(data?.resumes_summary || []).map((resume: AdminUserResumeSummary) => (
              <div key={resume.resumename} className="flex items-center justify-between text-sm py-1 border-b border-gray-100">
                <span>{resume.resumename}</span>
                <span className="text-xs text-gray-500">{resume.lastupdate ? new Date(resume.lastupdate).toLocaleDateString() : '-'}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
